import { MATERIALS, STATE_GAS, STEAM, WOOD, type World } from '@invaders/sim';
import type { Camera } from './camera';
import { rgbToAbgr, SHADES } from './palette';
import { THEMES, themedColor, type Theme } from './themes';

const OUTSIDE = rgbToAbgr(0x0d0e10);
/** GDD previews: the terrain darkens with depth under the open air, down to DEEP times its colour at DEPTH cells. */
const DEPTH = 96;
const DEEP = 0.55;
const LEVELS = 12;
/** Steam lets the sky show through a little. */
const STEAM_ALPHA = 0.8;

/**
 * Draws only the visible cells (GDD section 13): they are copied into a small image of the view's size,
 * which is then scaled up without smoothing. The fractional part of the camera becomes a pixel offset.
 * The empty cells are transparent: the Backdrop drawn before shows through.
 */
export class ViewRenderer {
  private readonly buffer = document.createElement('canvas');
  private readonly bctx = this.buffer.getContext('2d')!;
  private image: ImageData | null = null;
  private pixels = new Uint32Array(0);
  /** Entry (level << 10) | (material << 2) | shade: the material's colour, its shade, darkened to the depth level. */
  private readonly palette = new Uint32Array(LEVELS << 10);
  /**
   * 1 for the empty cell and the gases: the open air, from which the depth is counted. 2 for wood: it neither
   * counts nor resets (a tree or a floor would otherwise leave a dark stripe in the ground under it).
   */
  private readonly open = new Uint8Array(256);
  private readonly levelOf = new Uint8Array(DEPTH + 1);
  private depth = new Int32Array(0);

  constructor(theme: Theme = THEMES.island) {
    for (let d = 0; d <= DEPTH; d++) this.levelOf[d] = Math.min(LEVELS - 1, Math.floor((d * LEVELS) / (DEPTH + 1)));
    this.setTheme(theme);
  }

  setTheme(theme: Theme): void {
    // GDD "teinte": a slight variation of colour per cell, so that a material does not look flat (display only).
    const shades = [0.94, 0.98, 1.02, 1.06];
    MATERIALS.forEach((m, id) => {
      const open = id === 0 || m.state === STATE_GAS;
      this.open[id] = open ? 1 : id === WOOD ? 2 : 0;
      const rgb = themedColor(theme, id);
      const alpha = id === 0 ? 0 : id === STEAM ? Math.round(255 * STEAM_ALPHA) : 255;
      for (let lv = 0; lv < LEVELS; lv++) {
        const dark = open ? 1 : 1 - (1 - DEEP) * Math.pow(lv / (LEVELS - 1), 0.8);
        for (let k = 0; k < SHADES; k++) {
          const f = id === 0 ? 1 : shades[k] * dark;
          const ch = (v: number) => Math.min(255, Math.round(v * f));
          this.palette[(lv << 10) | (id << 2) | k] =
            ((alpha << 24) | (ch(rgb & 0xff) << 16) | (ch((rgb >> 8) & 0xff) << 8) | ch((rgb >> 16) & 0xff)) >>> 0;
        }
      }
    });
  }

  draw(ctx: CanvasRenderingContext2D, world: World, cam: Camera): void {
    const cols = Math.ceil(cam.spanW) + 1;
    const rows = Math.ceil(cam.spanH) + 1;
    if (!this.image || this.image.width !== cols || this.image.height !== rows) {
      this.buffer.width = cols;
      this.buffer.height = rows;
      this.image = this.bctx.createImageData(cols, rows);
      this.pixels = new Uint32Array(this.image.data.buffer);
      this.depth = new Int32Array(cols);
    }
    const { w, h, data } = world.physique;
    const x0 = Math.floor(cam.x);
    const y0 = Math.floor(cam.y);
    const px = this.pixels;
    const pal = this.palette;
    const open = this.open;
    const levelOf = this.levelOf;
    const depth = this.depth;
    // Depth at the top of the view: the closed cells right above it, up to DEPTH.
    for (let c = 0; c < cols; c++) {
      const x = x0 + c;
      let d = 0;
      if (x >= 0 && x < w) {
        for (let y = Math.min(y0, h) - 1; y >= 0 && d < DEPTH; y--) {
          const o = open[data[y * w + x]];
          if (o === 1) break;
          if (o === 0) d++;
        }
      }
      depth[c] = d;
    }
    for (let r = 0; r < rows; r++) {
      const y = y0 + r;
      const out = r * cols;
      if (y < 0 || y >= h) { px.fill(OUTSIDE, out, out + cols); continue; }
      const row = y * w;
      for (let c = 0; c < cols; c++) {
        const x = x0 + c;
        if (x < 0 || x >= w) { px[out + c] = OUTSIDE; continue; }
        const m = data[row + x];
        let d = depth[c];
        const o = open[m];
        if (o === 1) d = 0;
        else if (o === 0 && d < DEPTH) d++;
        depth[c] = d;
        // Shade from a hash of the position (display only): mixed so that it shows no regular pattern.
        let hs = Math.imul(x, 0x27d4eb2d) ^ Math.imul(y, 0x165667b1);
        hs = Math.imul(hs ^ (hs >>> 15), 0x85ebca6b);
        px[out + c] = pal[(levelOf[d] << 10) | (m << 2) | (hs >>> 30)];
      }
    }
    this.bctx.putImageData(this.image, 0, 0);
    ctx.imageSmoothingEnabled = false;
    const ox = -Math.round((cam.x - x0) * cam.scale);
    const oy = -Math.round((cam.y - y0) * cam.scale);
    ctx.drawImage(this.buffer, ox, oy, cols * cam.scale, rows * cam.scale);
  }
}
